/**
 * Wishlist
 * - Stores liked variants in localStorage as { handle, variant } objects.
 *   Identity is the variant id, so different variants of the same product are
 *   separate entries and render as separate cards.
 * - <wishlist-button> toggles the currently selected variant and reflects its state.
 *   On the product page it tracks the theme `variant:change` event so the heart
 *   reflects whether the *selected* variant is liked.
 * - <wish-list> renders the stored variants by fetching each card through the
 *   Section Rendering API (/products/{handle}?variant={id}&section_id=wishlist-product).
 * - <wishlist-count> keeps the header icon always visible and reveals its counter
 *   bubble only when the list is not empty.
 */
(function () {
  const STORAGE_KEY = 'health-bar:wishlist';
  const UPDATED_EVENT = 'wishlist:updated';
  const CARD_SECTION = 'wishlist-product';
  const QUICK_VIEW_EVENT = 'wishlist:quickview';
  const QUICK_VIEW_SECTION = 'wishlist-quick-view';

  const WishlistStore = {
    read() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const items = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(items)) return [];
        // Migrate legacy handle-only entries to the { handle, variant } shape.
        return items.map((item) =>
          typeof item === 'string' ? { handle: item, variant: null } : item
        );
      } catch (error) {
        return [];
      }
    },

    write(items) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (error) {
        // Ignore write errors (private mode, quota exceeded).
      }
    },

    has(variantId) {
      if (!variantId) return false;
      return this.read().some((item) => item.variant === variantId);
    },

    toggle(handle, variantId, meta) {
      const items = this.read();
      const index = items.findIndex((item) => item.variant === variantId);
      let active;

      if (index === -1) {
        items.push({ handle, variant: variantId });
        active = true;
      } else {
        items.splice(index, 1);
        active = false;
      }

      this.write(items);
      this.dispatch({ handle, variant: variantId, active, items, meta: meta || {} });
      return active;
    },

    remove(variantId) {
      const items = this.read().filter((item) => item.variant !== variantId);
      this.write(items);
      this.dispatch({ variant: variantId, active: false, items });
    },

    dispatch(detail) {
      document.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail }));
    }
  };

  function getRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Shopify preview/theme-editor params. Section Rendering API requests must
  // carry these so a preview or shared link fetches from the previewed theme
  // instead of the live one (otherwise unpublished sections return 404).
  const PREVIEW_PARAMS = ['preview_theme_id', '_fd', 'pb', '_ab', '_sd'];

  function applyPreviewParams(url) {
    const current = new URLSearchParams(window.location.search);
    PREVIEW_PARAMS.forEach((key) => {
      const value = current.get(key);
      if (value !== null) url.searchParams.set(key, value);
    });
  }

  // Adds a variant to the cart and refreshes the cart drawer. When the drawer is
  // not available it falls back to a regular form post to the cart. Returns true
  // when the item ended up in the cart via the drawer.
  async function addVariantToCart(variantId, quantity, button) {
    if (!variantId) return false;

    const miniCart = document.querySelector('mini-cart');
    const hasDrawer =
      typeof theme !== 'undefined' &&
      theme.routes &&
      theme.shopSettings &&
      theme.shopSettings.cartDrawer &&
      miniCart;

    if (!hasDrawer) {
      if (typeof Shopify !== 'undefined' && typeof theme !== 'undefined' && theme.routes) {
        Shopify.postLink(theme.routes.cart_add_url, { parameters: { id: variantId, quantity } });
      }
      return false;
    }

    if (button) {
      button.setAttribute('data-loading', '');
      button.setAttribute('disabled', '');
    }

    const sections = miniCart.getSectionsToRender().map((section) => section.id);
    const body = JSON.stringify({
      id: variantId,
      quantity,
      sections,
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(theme.routes.cart_add_url, { ...fetchConfig('javascript'), body });
      const parsed = await response.json();

      if (parsed.status === 422) {
        document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
          detail: { errorMessage: parsed.description }
        }));
        return false;
      }

      miniCart.renderContents(parsed);
      return true;
    } catch (error) {
      return false;
    } finally {
      if (button) {
        button.removeAttribute('data-loading');
        button.removeAttribute('disabled');
      }
    }
  }

  class WishlistButton extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.handleClick.bind(this);
      this.onWishlistUpdated = this.handleWishlistUpdated.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
    }

    get handle() {
      return this.getAttribute('data-product-handle');
    }

    get variantId() {
      if (this.currentVariantId) return this.currentVariantId;
      const raw = this.getAttribute('data-variant-id');
      return raw ? Number(raw) : null;
    }

    get productImage() {
      return this.currentImage || this.getAttribute('data-product-image') || '';
    }

    get productTitle() {
      return this.getAttribute('data-product-title') || '';
    }

    connectedCallback() {
      this.button = this.querySelector('[data-wishlist-toggle]');
      this.label = this.querySelector('[data-wishlist-label]');
      this.addEventListener('click', this.onClick);
      document.addEventListener(UPDATED_EVENT, this.onWishlistUpdated);
      if (this.hasAttribute('data-track-variant')) {
        document.addEventListener('variant:change', this.onVariantChange);
      }
      this.syncState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener(UPDATED_EVENT, this.onWishlistUpdated);
      document.removeEventListener('variant:change', this.onVariantChange);
    }

    handleClick(event) {
      event.preventDefault();
      if (!this.handle || !this.variantId) return;
      WishlistStore.toggle(this.handle, this.variantId, {
        image: this.productImage,
        title: this.productTitle
      });
    }

    handleVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (variant && variant.id) {
        this.setVariant(variant.id);
      }
    }

    // Rebind the button to a specific variant and refresh its liked state.
    // Used on product cards, where color swatches switch the variant on
    // hover/click without firing a `variant:change` event. An optional image
    // keeps the "added to wishlist" toast in sync with the selected variant.
    setVariant(variantId, image) {
      if (!variantId) return;
      this.currentVariantId = Number(variantId);
      if (image) this.currentImage = image;
      this.syncState();
    }

    handleWishlistUpdated() {
      this.syncState();
    }

    syncState() {
      const active = WishlistStore.has(this.variantId);
      this.toggleAttribute('data-active', active);
      if (!this.button) return;

      this.button.toggleAttribute('data-pressed', active);
      const label = active
        ? this.button.getAttribute('data-remove-label')
        : this.button.getAttribute('data-add-label');
      if (label && this.label) this.label.textContent = label;
    }
  }

  class WishList extends HTMLElement {
    constructor() {
      super();
      this.onWishlistUpdated = this.handleWishlistUpdated.bind(this);
      this.onClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.emptyState = this.querySelector('[data-wishlist-empty]');
      this.skeletonTemplate = this.querySelector('[data-wishlist-skeleton]');
      this.root = getRoot();
      document.addEventListener(UPDATED_EVENT, this.onWishlistUpdated);
      this.addEventListener('click', this.onClick);
      this.init();
    }

    // Import variants from a shared ?variants= link, then reflect the current
    // wishlist back into the URL so it can be copied and shared again.
    async init() {
      await this.importFromUrl();
      this.updateShareUrl();
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(UPDATED_EVENT, this.onWishlistUpdated);
      this.removeEventListener('click', this.onClick);
    }

    handleClick(event) {
      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        WishlistStore.remove(Number(removeButton.getAttribute('data-variant')));
        return;
      }

      const addButton = event.target.closest('[data-wishlist-add]');
      if (addButton) {
        event.preventDefault();

        // Products without variants skip the quick view and go straight to the cart.
        if (addButton.hasAttribute('data-direct-add')) {
          addVariantToCart(Number(addButton.getAttribute('data-variant-id')), 1, addButton);
          return;
        }

        document.dispatchEvent(new CustomEvent(QUICK_VIEW_EVENT, {
          detail: {
            handle: addButton.getAttribute('data-product-handle'),
            variant: Number(addButton.getAttribute('data-variant-id'))
          }
        }));
      }
    }

    handleWishlistUpdated(event) {
      const detail = event.detail || {};
      this.updateShareUrl();
      if (detail.active === false) {
        this.removeCard(detail.variant);
      } else if (detail.active === true) {
        this.appendCard(detail);
      }
    }

    // Keep the page URL in sync with the wishlist, e.g.
    // /pages/wishlist?variants=123,456 — copy it to share the list.
    updateShareUrl() {
      if (!window.history || typeof window.history.replaceState !== 'function') return;

      const variants = WishlistStore.read()
        .map((item) => item.variant)
        .filter(Boolean);

      const url = new URL(window.location.href);
      if (variants.length) {
        url.searchParams.set('variants', variants.join(','));
      } else {
        url.searchParams.delete('variants');
      }

      window.history.replaceState({}, '', url);
    }

    // Add the variants from a shared link to this visitor's wishlist. The link
    // only carries variant ids, so their product handles are resolved from
    // products.json (needed to render and fetch each card).
    async importFromUrl() {
      const raw = new URLSearchParams(window.location.search).get('variants');
      if (!raw) return;

      const variantIds = raw
        .split(',')
        .map((value) => Number(value.trim()))
        .filter(Boolean);
      if (!variantIds.length) return;

      const items = WishlistStore.read();
      const known = new Set(items.map((item) => item.variant));
      const missing = variantIds.filter((id) => !known.has(id));
      if (!missing.length) return;

      const handleByVariant = await this.fetchVariantHandleMap();

      let added = false;
      missing.forEach((id) => {
        const handle = handleByVariant.get(id);
        if (handle) {
          items.push({ handle, variant: id });
          added = true;
        }
      });

      if (added) {
        WishlistStore.write(items);
        WishlistStore.dispatch({ items });
      }
    }

    async fetchVariantHandleMap() {
      const map = new Map();
      try {
        const response = await fetch(`${this.root}products.json?limit=250`);
        if (!response.ok) return map;

        const data = await response.json();
        (data.products || []).forEach((product) => {
          (product.variants || []).forEach((variant) => {
            map.set(variant.id, product.handle);
          });
        });
      } catch (error) {
        // Ignore — unresolved variants are simply skipped.
      }
      return map;
    }

    async render() {
      if (!this.grid) return;

      const items = WishlistStore.read();
      if (!items.length) {
        this.showEmpty(true);
        return;
      }

      this.showEmpty(false);
      this.renderSkeletons(items.length);
      this.setAttribute('data-loading', '');

      const cards = await Promise.all(items.map((item) => this.fetchCard(item)));
      this.grid.innerHTML = '';
      cards.forEach((card) => {
        if (card) this.grid.appendChild(card);
      });

      this.removeAttribute('data-loading');
      if (!this.grid.children.length) this.showEmpty(true);
    }

    // Fill the grid with placeholder cards (one per stored item) while the real
    // cards are fetched, so the layout is reserved and the load feels instant.
    renderSkeletons(count) {
      if (!this.grid || !this.skeletonTemplate) return;

      this.grid.innerHTML = '';
      for (let i = 0; i < count; i += 1) {
        this.grid.appendChild(this.skeletonTemplate.content.cloneNode(true));
      }
    }

    async fetchCard(item) {
      try {
        let url = `${this.root}products/${item.handle}?section_id=${CARD_SECTION}`;
        if (item.variant) url += `&variant=${item.variant}`;

        const response = await fetch(url);
        if (!response.ok) return null;

        const text = await response.text();
        const template = document.createElement('template');
        template.innerHTML = text.trim();
        return template.content.querySelector('[data-wishlist-product]');
      } catch (error) {
        return null;
      }
    }

    async appendCard(detail) {
      if (!this.grid || this.grid.querySelector(`[data-variant="${detail.variant}"]`)) return;

      const card = await this.fetchCard({ handle: detail.handle, variant: detail.variant });
      if (!card) return;

      this.showEmpty(false);
      this.grid.appendChild(card);
    }

    removeCard(variantId) {
      if (!this.grid) return;

      const card = this.grid.querySelector(`[data-variant="${variantId}"]`);
      if (card) card.remove();
      if (!this.grid.children.length) this.showEmpty(true);
    }

    showEmpty(isEmpty) {
      if (this.grid) this.grid.toggleAttribute('hidden', isEmpty);
      if (this.emptyState) this.emptyState.toggleAttribute('hidden', !isEmpty);
    }
  }

  class WishlistToast extends HTMLElement {
    constructor() {
      super();
      this.onWishlistUpdated = this.handleWishlistUpdated.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.hideTimer = null;
      this.visibleFor = Number(this.getAttribute('data-duration')) || 4000;
    }

    connectedCallback() {
      this.media = this.querySelector('[data-wishlist-toast-media]');
      this.image = null;
      this.addEventListener('click', this.onClick);
      document.addEventListener(UPDATED_EVENT, this.onWishlistUpdated);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener(UPDATED_EVENT, this.onWishlistUpdated);
      this.clearTimer();
    }

    handleClick(event) {
      if (event.target.closest('[data-wishlist-toast-close]')) this.hide();
    }

    handleWishlistUpdated(event) {
      const detail = event.detail || {};
      // Only announce additions, never removals.
      if (detail.active !== true) return;
      this.show(detail.meta || {});
    }

    show(meta) {
      if (this.media) {
        if (meta.image) {
          if (!this.image) {
            this.image = document.createElement('img');
            this.image.className = 'wishlist-toast__image';
            this.image.width = 60;
            this.image.height = 60;
            this.media.appendChild(this.image);
          }
          this.image.src = meta.image;
          this.image.alt = meta.title || '';
          this.image.setAttribute('data-show', '');
        } else if (this.image) {
          this.image.remove();
          this.image = null;
        }
      }

      this.setAttribute('data-show', '');

      this.clearTimer();
      this.hideTimer = window.setTimeout(() => this.hide(), this.visibleFor);
    }

    hide() {
      this.removeAttribute('data-show');
      this.clearTimer();
    }

    clearTimer() {
      if (this.hideTimer) {
        window.clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    }
  }

  class WishlistQuickView extends HTMLElement {
    constructor() {
      super();
      this.onOpenRequest = this.handleOpenRequest.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.root = this.getAttribute('data-root') || getRoot();
      this.handle = null;
    }

    connectedCallback() {
      this.contentTarget = this.querySelector('[data-wishlist-qv-content-target]');
      document.addEventListener(QUICK_VIEW_EVENT, this.onOpenRequest);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      document.removeEventListener(QUICK_VIEW_EVENT, this.onOpenRequest);
      document.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
    }

    handleOpenRequest(event) {
      const detail = event.detail || {};
      if (!detail.handle) return;
      this.open(detail.handle, detail.variant);
    }

    handleClick(event) {
      if (event.target.closest('[data-wishlist-qv-close]')) {
        this.close();
        return;
      }

      const variantButton = event.target.closest('[data-wishlist-qv-variant]');
      if (variantButton) {
        if (variantButton.hasAttribute('data-active') || variantButton.hasAttribute('data-soldout')) return;
        this.load(Number(variantButton.getAttribute('data-variant-id')));
        return;
      }

      const addButton = event.target.closest('[data-wishlist-qv-add]');
      if (addButton && !addButton.hasAttribute('data-soldout')) {
        this.addToCart(addButton);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    open(handle, variantId) {
      this.handle = handle;
      // A fresh product: drop the previous content so the skeleton shows.
      if (this.contentTarget) this.contentTarget.innerHTML = '';
      this.setAttribute('data-show', '');
      this.lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      this.load(variantId);
    }

    close() {
      this.removeAttribute('data-show');
      this.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
    }

    // Locking the page scroll removes the scrollbar, which would widen the page
    // and shift it right under the drawer. Its width differs per browser, so it
    // is measured and given back as padding while the drawer is open.
    lockScroll() {
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--wishlist-qv-scrollbar', `${scrollbar}px`);
      document.documentElement.setAttribute('data-wishlist-qv-open', '');
    }

    unlockScroll() {
      document.documentElement.removeAttribute('data-wishlist-qv-open');
      document.documentElement.style.removeProperty('--wishlist-qv-scrollbar');
    }

    // Fetch the drawer content for a variant server-side, so price, unit price,
    // image and active states are always correct without client-side math.
    async load(variantId) {
      if (!this.contentTarget || !this.handle) return;

      // First load shows the skeleton; a variant switch dims the current content.
      const stateAttribute = this.contentTarget.firstElementChild ? 'data-switching' : 'data-loading';
      this.setAttribute(stateAttribute, '');

      try {
        const url = new URL(`${this.root}products/${this.handle}`, window.location.origin);
        url.searchParams.set('section_id', QUICK_VIEW_SECTION);
        if (variantId) url.searchParams.set('variant', variantId);
        applyPreviewParams(url);

        const response = await fetch(url);
        if (!response.ok) return;

        const text = await response.text();
        const template = document.createElement('template');
        template.innerHTML = text.trim();
        const content = template.content.querySelector('[data-wishlist-qv-content]');
        if (content) {
          this.contentTarget.innerHTML = '';
          this.contentTarget.appendChild(content);
        }
      } catch (error) {
        // Ignore fetch/parse errors.
      } finally {
        this.removeAttribute('data-loading');
        this.removeAttribute('data-switching');
      }
    }

    async addToCart(button) {
      const variantId = Number(button.getAttribute('data-variant-id'));
      const quantityInput = this.querySelector('[data-wishlist-qv-quantity]');
      const quantity = quantityInput ? Math.max(1, parseInt(quantityInput.value, 10) || 1) : 1;

      const added = await addVariantToCart(variantId, quantity, button);
      if (added) this.close();
    }
  }

  class WishlistCount extends HTMLElement {
    constructor() {
      super();
      this.onWishlistUpdated = this.handleWishlistUpdated.bind(this);
    }

    connectedCallback() {
      this.total = this.querySelector('[data-wishlist-total]');
      this.bubble = this.querySelector('[data-wishlist-bubble]');
      document.addEventListener(UPDATED_EVENT, this.onWishlistUpdated);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener(UPDATED_EVENT, this.onWishlistUpdated);
    }

    handleWishlistUpdated() {
      this.update();
    }

    update() {
      const count = WishlistStore.read().length;
      if (this.total) this.total.textContent = count;
      if (this.bubble) this.bubble.toggleAttribute('data-show', count > 0);
    }
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }

  if (!customElements.get('wish-list')) {
    customElements.define('wish-list', WishList);
  }

  if (!customElements.get('wishlist-toast')) {
    customElements.define('wishlist-toast', WishlistToast);
  }

  if (!customElements.get('wishlist-quick-view')) {
    customElements.define('wishlist-quick-view', WishlistQuickView);
  }

  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', WishlistCount);
  }
})();
